import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { useToast } from './ToastContext';

const QuizModal = ({ isOpen, onClose, course }) => {
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', ''], // Changed to 3 options instead of 4
    correctAnswer: 0
  });
  const [quizName, setQuizName] = useState('');
  const [existingQuizNames, setExistingQuizNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [expandedQuizzes, setExpandedQuizzes] = useState({});
  const [leftPanelExpanded, setLeftPanelExpanded] = useState(false);
  const [editingQuizName, setEditingQuizName] = useState(null);
  const [newQuizName, setNewQuizName] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  // Load existing questions when modal opens
  useEffect(() => {
    if (isOpen && course) {
      // Reset modal state when opening
      setLeftPanelExpanded(false);
      setEditingQuizName(null);
      setNewQuizName('');
      setQuizName('');
      setNewQuestion({
        question: '',
        options: ['', '', ''],
        correctAnswer: 0
      });
      loadQuestions();
    }
  }, [isOpen, course]);

  const loadQuestions = async () => {
    if (!course) return;
    
    setQuestionsLoading(true);
    try {
      const q = query(collection(firestore, 'quizzes'), where('courseId', '==', course.id));
      const snapshot = await getDocs(q);
      const loadedQuestions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(loadedQuestions);
      
      // Extract unique quiz names
      const quizNames = new Set();
      loadedQuestions.forEach(question => {
        if (question.quizName) {
          quizNames.add(question.quizName);
        }
      });
      const uniqueQuizNames = Array.from(quizNames);
      setExistingQuizNames(uniqueQuizNames);
      
      // Initialize expanded state for all quizzes (default to collapsed)
      const initialExpandedState = {};
      uniqueQuizNames.forEach(name => {
        initialExpandedState[name] = false; // Start with all quizzes collapsed
      });
      setExpandedQuizzes(initialExpandedState);
    } catch (error) {
      console.error('Error loading questions:', error);
      showError('Failed to load questions');
    }
    setQuestionsLoading(false);
  };
  
  // Function to toggle quiz expansion
  const toggleQuizExpansion = (quizName) => {
    setExpandedQuizzes(prev => ({
      ...prev,
      [quizName]: !prev[quizName]
    }));
  };
  
  // Function to select a quiz for editing
  const selectQuizForEditing = (selectedQuizName) => {
    setQuizName(selectedQuizName);
    // Focus on the quiz name input field to show the user where the name is set
    setTimeout(() => {
      const quizNameInput = document.querySelector('input[placeholder*="Enter unique quiz name"]');
      if (quizNameInput) {
        quizNameInput.focus();
        // Scroll to the input field if needed
        quizNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    showSuccess(`Selected "${selectedQuizName}" for editing. Add new questions to this quiz using the form.`);
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.question.trim()) {
      showError('Please enter a question');
      return;
    }

    if (newQuestion.options.some(option => !option.trim())) {
      showError('Please fill all options');
      return;
    }
    
    // Encourage but don't require a unique quiz name
    if (!quizName.trim()) {
      // If no quiz name is provided, we'll use a default but notify the user
      showError('Please enter a quiz name for better organization');
      return;
    }

    setLoading(true);
    try {
      const questionData = {
        ...newQuestion,
        courseId: course.id,
        courseName: course.name, // Add courseName field for student queries
        quizName: quizName.trim(),
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(firestore, 'quizzes'), questionData);
      
      // Reset form
      setNewQuestion({
        question: '',
        options: ['', '', ''],
        correctAnswer: 0
      });
      
      // Don't reset quizName - keep it for adding more questions to the same quiz
      
      showSuccess('Question added successfully!');
      
      // Reload questions to show the new one
      await loadQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
      showError('Failed to add question');
    }
    setLoading(false);
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await deleteDoc(doc(firestore, 'quizzes', questionId));
      showSuccess('Question deleted successfully!');
      
      // Reload questions to reflect the deletion
      await loadQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      showError('Failed to delete question');
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: newOptions });
  };

  // Function to start editing quiz name
  const startEditingQuizName = (quizName) => {
    setEditingQuizName(quizName);
    setNewQuizName(quizName);
  };

  // Function to save quiz name changes
  const saveQuizName = async () => {
    if (!newQuizName.trim() || newQuizName.trim() === editingQuizName) {
      setEditingQuizName(null);
      setNewQuizName('');
      return;
    }

    if (existingQuizNames.includes(newQuizName.trim()) && newQuizName.trim() !== editingQuizName) {
      showError('Quiz name already exists. Please choose a different name.');
      return;
    }

    setEditingLoading(true);
    try {
      const batch = writeBatch(firestore);
      
      // Update all questions with the old quiz name
      const questionsToUpdate = questions.filter(q => q.quizName === editingQuizName);
      
      questionsToUpdate.forEach(question => {
        const questionRef = doc(firestore, 'quizzes', question.id);
        batch.update(questionRef, { quizName: newQuizName.trim() });
      });

      await batch.commit();
      
      // Update local state
      setQuestions(prev => prev.map(q => 
        q.quizName === editingQuizName 
          ? { ...q, quizName: newQuizName.trim() }
          : q
      ));
      
      // Update existing quiz names
      setExistingQuizNames(prev => {
        const updated = prev.filter(name => name !== editingQuizName);
        return [...updated, newQuizName.trim()];
      });

      // Update expanded state
      setExpandedQuizzes(prev => {
        const newState = { ...prev };
        if (newState[editingQuizName] !== undefined) {
          newState[newQuizName.trim()] = newState[editingQuizName];
          delete newState[editingQuizName];
        }
        return newState;
      });

      showSuccess(`Quiz name updated from "${editingQuizName}" to "${newQuizName.trim()}"`);
      setEditingQuizName(null);
      setNewQuizName('');
    } catch (error) {
      console.error('Error updating quiz name:', error);
      showError('Failed to update quiz name');
    }
    setEditingLoading(false);
  };

  // Function to cancel editing quiz name
  const cancelEditingQuizName = () => {
    setEditingQuizName(null);
    setNewQuizName('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] max-w-7xl">
        {/* Header */}
        <div className="bg-purple-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Quiz Management</h2>
            <p className="text-purple-100">Course: {course?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-purple-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Add New Question */}
          <div className={`${leftPanelExpanded ? 'w-1/2' : 'w-16'} transition-all duration-300 ease-in-out border-r border-gray-200 overflow-hidden`}>
            {leftPanelExpanded ? (
              <div className="p-6 overflow-y-auto h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Add New Question</h3>
                  <button
                    onClick={() => setLeftPanelExpanded(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded transition-colors"
                    title="Collapse Panel"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Quiz Name Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quiz Name <span className="text-purple-600 font-medium">(Recommended for random assignment)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={quizName}
                        onChange={(e) => setQuizName(e.target.value)}
                        placeholder="Enter unique quiz name"
                        className={`w-full p-3 border ${!quizName.trim() ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      />
                      {!quizName.trim() && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 flex items-center">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {existingQuizNames.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-1">Existing quiz names for this course:</p>
                        <div className="flex flex-wrap gap-2">
                          {existingQuizNames.map((name, index) => (
                            <span key={index} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Question Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question *
                    </label>
                    <textarea
                      value={newQuestion.question}
                      onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                      placeholder="Enter your question here..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      rows="3"
                    />
                  </div>

                  {/* Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options *
                    </label>
                    {newQuestion.options.map((option, index) => (
                      <div key={index} className="flex items-center mb-2">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={newQuestion.correctAnswer === index}
                          onChange={() => setNewQuestion({ ...newQuestion, correctAnswer: index })}
                          className="mr-3 text-purple-600 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                  </div>

                  {/* New Quiz Button */}
                  <button
                    onClick={() => {
                      // Generate a temporary quiz name if none exists
                      const tempQuizName = 'New Quiz ' + (existingQuizNames.length + 1);
                      
                      // Clear the quiz name input and set focus
                      setQuizName('');
                      document.querySelector('input[placeholder*="Enter unique quiz name"]')?.focus();
                      
                      // Show a toast notification
                      showSuccess('Enter a name for your new quiz in the field above.');
                      
                      // If this quiz name doesn't already exist, add it to the list
                      if (!existingQuizNames.includes(tempQuizName)) {
                        // Update the existing quiz names list
                        setExistingQuizNames(prev => [...prev, tempQuizName]);
                        
                        // Set the expanded state for this new quiz
                        setExpandedQuizzes(prev => ({
                          ...prev,
                          [tempQuizName]: true
                        }));
                        
                        // Create an empty placeholder for this quiz in the questions list
                        // This ensures the quiz appears in the right panel even before adding questions
                        setQuestions(prev => [
                          ...prev,
                          {
                            id: 'temp-' + Date.now(), // Temporary ID that won't conflict with Firestore IDs
                            quizName: tempQuizName,
                            question: 'Click "Edit Quiz" to start adding questions to this quiz',
                            options: ['No questions added yet'],
                            correctAnswer: 0,
                            isPlaceholder: true // Flag to identify this as a placeholder
                          }
                        ]);
                      }
                      
                      // Set the quiz name for editing
                      setTimeout(() => {
                        setQuizName(tempQuizName);
                      }, 300);
                    }}
                    className="w-full mb-3 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Quiz
                  </button>
                  
                  {/* Add Question Button */}
                  <button
                    onClick={handleAddQuestion}
                    disabled={loading}
                    className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Adding...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Question
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Collapsed State */
              <div className="h-full flex flex-col items-center justify-center">
                <button
                  onClick={() => setLeftPanelExpanded(true)}
                  className="bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition-colors"
                  title="Expand Add Question Panel"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">Add Question</p>
              </div>
            )}
          </div>

          {/* Right Panel - Existing Questions */}
          <div className={`${leftPanelExpanded ? 'w-1/2' : 'flex-1'} p-6 overflow-y-auto`}>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Existing Questions ({questions.length})</h3>
            
            {questionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="w-8 h-8 animate-spin text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No questions added yet.</p>
                <p className="text-sm">Add your first question using the form on the left.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Group questions by quiz name */}
                {Object.entries(questions.reduce((groups, question) => {
                  const quizName = question.quizName || 'Unnamed Quiz';
                  if (!groups[quizName]) groups[quizName] = [];
                  groups[quizName].push(question);
                  return groups;
                }, {})).map(([quizName, quizQuestions]) => (
                  <div key={quizName} className="border border-purple-200 rounded-lg overflow-hidden">
                    {/* Quiz Name Header - Clickable for expand/collapse */}
                    <div 
                      className="bg-purple-100 p-3 border-b border-purple-200 cursor-pointer flex items-center justify-between"
                      onClick={() => toggleQuizExpansion(quizName)}
                    >
                      <div className="flex items-center">
                        {/* Expand/Collapse Icon */}
                        <svg 
                          className={`w-5 h-5 mr-2 transition-transform ${expandedQuizzes[quizName] ? 'transform rotate-90' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        
                        {/* Quiz Name - Editable */}
                        {editingQuizName === quizName ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={newQuizName}
                              onChange={(e) => setNewQuizName(e.target.value)}
                              className="px-2 py-1 border border-purple-300 rounded text-sm font-semibold text-purple-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              autoFocus
                            />
                            <button
                              onClick={saveQuizName}
                              disabled={editingLoading}
                              className="bg-green-500 hover:bg-green-600 text-white text-xs py-1 px-2 rounded transition-colors disabled:opacity-50"
                            >
                              {editingLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={cancelEditingQuizName}
                              className="bg-gray-500 hover:bg-gray-600 text-white text-xs py-1 px-2 rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <h4 className="font-semibold text-purple-800">
                            {quizName} <span className="text-sm font-normal text-purple-600">({quizQuestions.length} questions)</span>
                          </h4>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {/* Edit Quiz Name Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingQuizName(quizName);
                          }}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs py-1 px-2 rounded transition-colors"
                          title="Edit Quiz Name"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        {/* Button to select this quiz for editing */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering the parent onClick
                            selectQuizForEditing(quizName);
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-2 rounded transition-colors"
                        >
                          Edit Quiz
                        </button>
                      </div>
                    </div>
                    
                    {/* Quiz Questions - Collapsible */}
                    {expandedQuizzes[quizName] && (
                      <div className="divide-y divide-gray-100">
                        {quizQuestions.map((question, index) => (
                          <div key={question.id} className="bg-white p-4">
                            {question.isPlaceholder ? (
                              <div className="text-center py-4">
                                <div className="text-blue-500 mb-2">
                                  <svg className="w-10 h-10 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <p className="text-gray-600 mb-2">{question.question}</p>
                                <button
                                  onClick={() => selectQuizForEditing(quizName)}
                                  className="bg-purple-600 text-white text-sm py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                  Add Questions to "{quizName}"
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-start justify-between mb-3">
                                  <h4 className="font-semibold text-gray-800">
                                    Q{index + 1}: {question.question}
                                  </h4>
                                  <button
                                    onClick={() => handleDeleteQuestion(question.id)}
                                    className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                                    title="Delete Question"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <div className="space-y-2">
                                  {!question.isPlaceholder && question.options.map((option, optionIndex) => (
                                    <div
                                      key={optionIndex}
                                      className={`flex items-center p-2 rounded ${
                                        optionIndex === question.correctAnswer
                                          ? 'bg-green-100 border border-green-300'
                                          : 'bg-white border border-gray-200'
                                      }`}
                                    >
                                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${
                                        optionIndex === question.correctAnswer
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-300 text-gray-600'
                                      }`}>
                                        {String.fromCharCode(65 + optionIndex)}
                                      </span>
                                      <span className={optionIndex === question.correctAnswer ? 'font-semibold text-green-800' : 'text-gray-700'}>
                                        {option}
                                      </span>
                                      {optionIndex === question.correctAnswer && (
                                        <span className="ml-auto text-green-600">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
